import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Quote, Plus, Trash2, Upload, Loader2, Sparkles, CalendarDays } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ThoughtLibraryPage = () => {
  const [data, setData] = useState({ thoughts: [], total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [newText, setNewText] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [newPinnedDate, setNewPinnedDate] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [busy, setBusy] = useState(false);
  const [starterBusy, setStarterBusy] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [list, t] = await Promise.all([
        axios.get(`${API_URL}/api/thoughts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/thoughts/today`, { withCredentials: true }),
      ]);
      setData(list.data);
      setToday(t.data);
    } catch {
      toast.error('Failed to load thoughts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addOne = async () => {
    if (!newText.trim()) { toast.error('Enter a thought'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/thoughts`, { text: newText, author: newAuthor, pinned_date: newPinnedDate || null }, { withCredentials: true });
      toast.success(newPinnedDate ? `Thought pinned to ${newPinnedDate}` : 'Thought added');
      setNewText(''); setNewAuthor(''); setNewPinnedDate('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const importStarter = async () => {
    setStarterBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/thoughts/starter-pack`, {}, { withCredentials: true });
      toast.success(`Starter pack imported — ${res.data.added} added${res.data.skipped ? `, ${res.data.skipped} already present` : ''}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setStarterBusy(false); }
  };

  const addBulk = async () => {
    if (!bulkText.trim()) { toast.error('Paste some thoughts'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/thoughts/bulk`, { text: bulkText }, { withCredentials: true });
      toast.success(`${res.data.added} thoughts added`);
      setBulkText('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API_URL}/api/thoughts/${t.id}`, { active: !t.active }, { withCredentials: true });
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/thoughts/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="thought-library-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Quote className="w-6 h-6 text-primary" /> Thought Library</h1>
        <p className="text-muted-foreground text-sm mt-1">Feed the daily thoughts shown on every employee's dashboard. One rotates automatically each day; pin a thought to a date for festivals or special occasions.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={importStarter} disabled={starterBusy} variant="secondary" className="gap-2" data-testid="import-starter-btn">
          {starterBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Import starter pack (50)
        </Button>
      </div>

      {today && (
        <Card className="border-l-4 border-l-primary bg-primary/5" data-testid="today-thought-preview">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Showing Today</p>
            <p className="text-base font-medium mt-1">{today.text}{today.author ? ` — ${today.author}` : ''}</p>
            {today.is_default && <p className="text-xs text-amber-600 mt-1">Default fallback — add thoughts below to replace it.</p>}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" /> Add a Thought</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Textarea value={newText} onChange={e => setNewText(e.target.value)} placeholder="Enter an inspiring thought…" rows={3} data-testid="new-thought-text" />
            <Input value={newAuthor} onChange={e => setNewAuthor(e.target.value)} placeholder="Author (optional)" data-testid="new-thought-author" />
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Pin to a date (optional) — e.g. a festival greeting</label>
              <Input type="date" value={newPinnedDate} onChange={e => setNewPinnedDate(e.target.value)} data-testid="new-thought-pinned-date" />
            </div>
            <Button onClick={addOne} disabled={busy} className="gap-2" data-testid="add-thought-btn"><Plus className="w-4 h-4" /> Add</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> Bulk Import</CardTitle>
            <CardDescription>One thought per line. Use "Thought — Author" to include an author.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea value={bulkText} onChange={e => setBulkText(e.target.value)} placeholder={"Progress, not perfection.\nStay curious — Anonymous"} rows={5} data-testid="bulk-thought-text" />
            <Button onClick={addBulk} disabled={busy} variant="outline" className="gap-2" data-testid="bulk-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import All</Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Thoughts <span className="text-sm text-muted-foreground font-normal">· {data.active} active / {data.total} total</span></CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : data.thoughts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No thoughts yet — add some above.</p>
          ) : (
            <div className="divide-y">
              {data.thoughts.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`thought-row-${t.id}`}>
                  <div className="min-w-0">
                    <p className={`text-sm ${t.active ? '' : 'text-muted-foreground line-through'}`}>{t.text}</p>
                    <div className="flex items-center gap-2">
                      {t.author && <p className="text-xs text-muted-foreground">— {t.author}</p>}
                      {t.pinned_date && <Badge className="text-[10px] gap-1 bg-primary/10 text-primary border-0"><CalendarDays className="w-3 h-3" /> Pinned {t.pinned_date}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!t.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    <Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`toggle-thought-${t.id}`} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(t.id)} data-testid={`delete-thought-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ThoughtLibraryPage;
